// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { sparseProjectSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

export const assetProjectHistorySegmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  start: dateTimeSchema,
  end: dateTimeSchema.nullable(),
  project: sparseProjectSchema,
});

export type AssetProjectHistorySegment = z.infer<
  typeof assetProjectHistorySegmentSchema
>;

const assetProjectHistoryResponseSchema = z
  .object({
    data: z.array(assetProjectHistorySegmentSchema),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, history: data }));

const assetProjectHistorySegmentResponseSchema = z
  .object({
    data: assetProjectHistorySegmentSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, segment: data }));

/**
 * Requests the project history of an asset from the remote API.
 *
 * @param key - The key of the asset for which to request history.
 */
export async function getAssetProjectHistory(key: string) {
  const path = `/v1/asset/${key}/project-history`;
  const response = await api.get(path);

  return parseResponse(assetProjectHistoryResponseSchema, response.data, {
    schemaName: 'AssetProjectHistory',
    endpoint: path,
  });
}

/**
 * Retrieves a specific segment of the project history for a given asset.
 *
 * @param {string} assetKey - The unique identifier of the asset whose project history segment is being retrieved.
 * @param {string} segmentKey - The unique identifier of the specific segment of the project history.
 * @return {Promise<Object>} A promise that resolves with the parsed response containing the segment of the project history.
 */
export async function getAssetProjectHistorySegment(
  assetKey: string,
  segmentKey: string
) {
  const path = `/v1/asset/${assetKey}/project-history/${segmentKey}`;
  const response = await api.get(path);

  return parseResponse(
    assetProjectHistorySegmentResponseSchema,
    response.data,
    {
      schemaName: 'AssetProjectHistorySegment',
      endpoint: path,
    }
  );
}
